import React from 'react';
import PropTypes from 'prop-types';

import { FormControl, Switch, Typography, Button, ListItemButton, Grid, Tooltip } from '@mui/material';
import { withStyles } from '@mui/styles';

import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer';
import VirtualizedList from 'react-virtualized/dist/commonjs/List';

const rowHeight = 24;
const styles = {
	actionRow: {
		width: '100%',
		height: `${rowHeight}px`,
		padding: '0 4px',
		transition: 'none !important',
	},
	textRow: {
		paddingLeft: '4px',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		fontFamily: 'monospace !important',
		fontSize: '13px !important',
		lineHeight: `${rowHeight}px !important`,
	},
	col: {
		display: 'flex',
		paddingRight: 4,
		height: '100%',
		flexShrink: '1',
	},
	colWithPadding: {
		paddingLeft: 4,
	},
	colNoPadding: {
		paddingLeft: 0,
	},
	colNoGrow: {
		flexGrow: '0',
	},
	colGrow: {
		flexGrow: '1',
	},
	colNoShrink: {
		flexShrink: '0',
	},
	colShrink: {
		flexShrink: '1',
	}
};

class DetailsItemList extends React.PureComponent {
	constructor(props) {
		super(props);
		this.state = {
			selectedIndex: null,
			items: [],
			tailing: true,
			scrollToIndex: undefined,
		};
		this.rowRenderer = this.rowRenderer.bind(this);
		this.itemBuffer = [];

		this.onScroll = this.onScroll.bind(this);
		this.onKeyDown = this.onKeyDown.bind(this);
		this.onKeyUp = this.onKeyUp.bind(this);

		this.currentSelected = null;

		this.kbrdNavTimeout = null;
		this.notifyTimeout = null;
	}

	addItem(item, flush) {
		this.itemBuffer.push(item);

		if (flush) {
			if (!this.flushTimeout) {
				this.flushTimeout = setTimeout(() => {
					const items = [...this.state.items, ...this.itemBuffer];

					this.flushTimeout = null;
					this.itemBuffer = [];

					if (this.state.tailing) {
						this.setState({ items, selectedIndex: items.length - 1 });
						this.props.onItemClick(item);
					} else {
						this.setState({ items });
					}
				}, 100);
			}
		}
	}

	onItemClick(index, delay = 0, tailing=false) {
		this.currentSelected = index;
		this.setState({ selectedIndex: index, tailing, scrollToIndex: index });
		if (delay) {
			if (!this.notifyTimeout) {
				this.notifyTimeout = setTimeout(() => {
					this.props.onItemClick(this.state.items[this.state.selectedIndex]);
					this.notifyTimeout = null;
				}, delay);
			}
		} else {
			this.props.onItemClick(this.state.items[index]);
		}
	}

	onScroll({ clientHeight, scrollHeight, scrollTop }) {
		// note: scroll is at the bottom of the page (with 10px margin)
		let tailing = scrollTop >= scrollHeight - clientHeight - 10;
		if ((tailing !== this.state.tailing) && (this.state.scrollToIndex !== this.state.items.length - 1 || !this.holdingArrowKey)) {
			this.setState({ tailing: tailing, scrollToIndex: undefined });
		}
	}

	clear() {
		this.setState({ items: [] });
	}

	onKeyDown(event) {
		let dir = event.key === 'ArrowDown' ? 1 : event.key === 'ArrowUp' ? -1 : 0;
		if(dir) {
			let nextIdx = (
				this.currentSelected !== null && this.currentSelected >= 0 && this.currentSelected < this.state.items.length ?
					this.currentSelected + dir :
					dir > 0 ?
						this.state.items.length - 1 :
						0
			);
			if(nextIdx >= 0 && nextIdx < this.state.items.length) {
				this.currentSelected = nextIdx;
				if (!this.kbrdNavTimeout) {
					let holding = this.holdingArrowKey;
					this.kbrdNavTimeout = setTimeout(() => {
						this.onItemClick(this.currentSelected, holding ? 400 : 0);
						this.kbrdNavTimeout = null;
					}, 10);
				}
			}
			this.holdingArrowKey = true;
			event.preventDefault();
		}
	}

	onKeyUp(event) {
		if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
			this.holdingArrowKey = false;
		}
	}

	rowRenderer({ index, key, style }) {
		let item = this.state.items[index];
		let summary = this.props.getSummary(item);
		return (
			<ListItemButton disableRipple key={key} divider style={style} className={this.props.classes.actionRow} onClick={() => { if (this.rootRef) this.rootRef.focus(); this.onItemClick(index); }} selected={index === this.state.selectedIndex}>
				<Typography variant="body1" className={`${this.props.classes.textRow} ${this.props.classes.col} ${this.props.classes.colNoPadding}`} style={{ flexBasis: '40px', borderRight: this.props.theme.border }}>
					{index}
				</Typography>
				{summary.map((item, itemIdx) => {
					let [flexBasis, allowGrow = false, allowShrink = true, withPadding = true] = this.props.columnSizes[itemIdx];
					let colStyle = {
						flexBasis,
					};
					let last = itemIdx === summary.length - 1;
					if (!last) {
						colStyle['borderRight'] = this.props.theme.border;
					}
					let colGrowClass = allowGrow ? this.props.classes.colGrow : this.props.classes.colNoGrow;
					let colShrinkClass = allowShrink ? this.props.classes.colShrink : this.props.classes.colNoShrink;
					let colPaddingClass = withPadding ? this.props.classes.colWithPadding : this.props.classes.colNoPadding;
					return (
						<Typography key={itemIdx} variant="body1" className={`${this.props.classes.textRow} ${this.props.classes.col} ${colPaddingClass} ${colGrowClass} ${colShrinkClass}`} style={colStyle}>
							{item}
						</Typography>
					);
				})}
			</ListItemButton>
		);
	}

	render() {
		return (
			<div style={{ display: 'flex', flexFlow: 'column', height: '100%', overflow: 'hidden', background: this.props.theme.palette.background.paper, outline: 0 }}
				tabIndex="0" ref={(ref) => { this.rootRef = ref; }} onKeyDown={this.onKeyDown} onKeyUp={this.onKeyUp}
			>
				<div style={{ flex: '0 1 auto', background: this.props.theme.palette.background.header, borderBottom: this.props.theme.border }}>
					<Grid container item justifyContent="flex-end" alignItems="center" direction="row" spacing={2}>
						{this.props.bonus !== undefined && (
							<Grid item>
								{this.props.bonus}
							</Grid>)}
						<Grid item>
							<Button color="primary" onClick={() => this.clear()}>Clear</Button>
						</Grid>
						<Grid item>
							<FormControl>
								<Tooltip title={'"Tail" logs'}>
									<Switch
										checked={this.state.tailing}
										onChange={(event) => { this.setState({ tailing: event.target.checked }); }}
										style={{ height: 36 }}
									/>
								</Tooltip>
							</FormControl>
						</Grid>
					</Grid>
				</div>
				<div style={{ overflow: 'none', flex: '1 1 auto' }}>
					<AutoSizer>
						{({ height, width }) => (
							<VirtualizedList
								style={{ overflowY: 'auto', outline: 0 }}
								height={height}
								width={width}

								rowCount={this.state.items.length}
								rowHeight={rowHeight}
								rowRenderer={this.rowRenderer}

								onScroll={this.onScroll}
								scrollToIndex={this.state.tailing ? this.state.items.length - 1 : this.state.scrollToIndex}

								selectedIndex={this.state.selectedIndex}
								tailing={this.state.tailing}
							/>
						)}
					</AutoSizer>
				</div>
			</div>);
	}
}

DetailsItemList.propTypes = {
	getSummary: PropTypes.func,
	onItemClick: PropTypes.func,
	columnSizes: PropTypes.array,
	bonus: PropTypes.node,

	theme: PropTypes.object,
	classes: PropTypes.object,
};

export default withStyles(styles, { withTheme: true })(DetailsItemList);
